import { useForm } from 'react-hook-form';

import { Button } from '@crehana/compass.ui/button';
import { Loader } from '@crehana/compass.ui/loader';
import { useTranslation } from '@crehana/i18n';
import { TextField } from '@crehana/ui';

import { FormSubmitButton } from '@/views/Auth/components';
import { validations } from '@/views/Auth/constants';

import { useChangePassword } from './hooks/use-change-password';
import { useLearningSession } from './hooks/use-learning-session';

type TFormValues = {
  password: string;
  confirmPassword: string;
};

type ChangePasswordFormProps = {
  onSuccess: () => void;
};

export const ChangePasswordForm = ({ onSuccess }: ChangePasswordFormProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TFormValues>();
  const currentPassword = watch('password', '');

  const { changePassword } = useChangePassword();
  const { learningSessionData, loading, error, refetch } = useLearningSession();

  // TODO: remove after this form is working correctly in production
  console.log('learning session', { loading, error, learningSessionData });

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-8">
        <Loader />
      </div>
    );
  }

  if (error) {
    console.error(error);
    return (
      <div>
        <p className="tw-font-body4 tw-font-semibold tw-mb-1.2 tw-text-alert-500">
          {t('LEARNING_SESSION_QUERY_ERROR')}
        </p>
        <Button
          type="button"
          onClick={() => {
            refetch();
          }}
        >
          {t('RETRY')}
        </Button>
      </div>
    );
  }

  if (!learningSessionData) {
    return (
      <div>
        <p className="tw-font-body4 tw-font-semibold tw-mb-1.2 tw-text-alert-500">
          {t('LEARNING_SESSION_NOT_FOUND')}
        </p>
        <Button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
        >
          {t('RELOAD_PAGE')}
        </Button>
      </div>
    );
  }

  const onSubmit = (data: TFormValues) =>
    changePassword({
      variables: {
        input: {
          password: data.password,
          confirm_password: data.confirmPassword,
          user_id: learningSessionData.userLearningId,
          organization_id: learningSessionData.organizationLearningId,
        },
      },
    }).then(response => {
      // success
      if (
        response.data?.security.change_password_user.__typename ===
        'ChangePasswordUserChangedPasswordPayload'
      ) {
        onSuccess();
        return;
      }

      setError('password', {
        type: 'manual',
        message: t('CHANGE_PASSWORD_ERROR_MESSAGE'),
      });
    });

  // We are using the syntax [&>div>input] to target the input element inside the wrapper
  // that's because the input doesn't have a prop to override it
  const inputWrapperClassName =
    'rounded-10 [&>div>input]:tw-font-body4 md:[&>div>input]:tw-font-body3 [&>div>input]:tw-py-0 md:[&>div>input]:tw-py-1.6 [&&&]:tw-border-neutral-light-500 md:[&&&]:border-dark-gray';

  const inputLabelClassName =
    '[&>span]:tw-font-subtitle5 md:[&>span]:tw-font-subtitle4';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-mb-4 md:tw-mb-2.4">
        <h3 className="tw-font-h2 tw-mb-1.2">
          {t('CHANGE_PASSWORD_FORM_TITLE')}
        </h3>
        <p className="tw-font-body3">{t('CHANGE_PASSWORD_FORM_DESCRIPTION')}</p>
      </div>
      <TextField
        className={`tw-mb-2.8 md:tw-mb-2.4 ${inputLabelClassName}`}
        wrapperClassName={inputWrapperClassName}
        id="password"
        name="password"
        type="password"
        label={t('CHANGE_PASSWORD_FORM_INPUT_PASSWORD_LABEL')}
        hint={t('CHANGE_PASSWORD_FORM_INPUT_PASSWORD_HINT')}
        ref={register({
          required: validations.required(t),
          minLength: validations.minLength(t),
          maxLength: validations.maxLength(t),
          validate: {
            noSpaces: value =>
              !value.includes(' ') || t('PASSWORD_CANNOT_CONTAIN_SPACES'),
          },
        })}
        disabled={isSubmitting}
        errorText={errors.password?.message}
        full
      />
      <TextField
        className={`tw-mb-3.2 md:tw-mb-2.4 ${inputLabelClassName}`}
        wrapperClassName={inputWrapperClassName}
        id="confirmPassword"
        name="confirmPassword"
        type="password"
        label={t('CHANGE_PASSWORD_FORM_INPUT_CONFIRM_PASSWORD_LABEL')}
        hint={t('CHANGE_PASSWORD_FORM_INPUT_CONFIRM_PASSWORD_HINT')}
        ref={register({
          required: validations.required(t),
          validate: {
            matchPassword: value =>
              validations.matchPassword(t, currentPassword).validate(value),
            noSpaces: value =>
              !value.includes(' ') || t('PASSWORD_CANNOT_CONTAIN_SPACES'),
          },
        })}
        disabled={isSubmitting}
        errorText={errors.confirmPassword?.message}
        full
      />
      <FormSubmitButton
        label={t(
          isSubmitting
            ? 'CHANGE_PASSWORD_FORM_BUTTON_SUBMIT_LABEL_LOADING'
            : 'CHANGE_PASSWORD_FORM_BUTTON_SUBMIT_LABEL',
        )}
        disabled={isSubmitting}
      />
    </form>
  );
};
